/**
 * Bug Report Dialog Component
 *
 * Modal dialog for submitting bug reports with screenshot annotation.
 * Includes form validation, Turnstile verification, and screenshot capture.
 *
 * @module BugReportDialog
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ScreenshotCanvas } from './ScreenshotCanvas';
import { TurnstileWidget } from './TurnstileWidget';
import { collectMetadata } from './utils/metadata';
import { captureViewport } from './utils/screenshot';
import { submitBugReport, getErrorMessage, type SubmitBugReportData } from './api-client';
import { sanitizeFormData } from './utils/xss-sanitizer';
import { Portal } from './utils/portal';
import { useAttachments } from './useAttachments';
import { DropZone } from './DropZone';
import { AttachmentGrid } from './AttachmentGrid';
import { AttachmentPreview } from './AttachmentPreview';
import type { WidgetConfig, AttachmentType, ClientAttachment } from '@wds/bug-reporter-types';

/**
 * Convert base64 data URL to File object
 */
function dataUrlToFile(dataUrl: string, filename: string): File {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const base64 = parts[1];

  // Decode base64
  const byteString = atob(base64);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new File([arrayBuffer], filename, { type: mime });
}

/**
 * Calculate relative luminance to determine if color is light or dark
 */
function isLightColor(hexColor: string): boolean {
  if (hexColor === 'transparent') return true;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.6;
}

// Session storage key for persisting email
const EMAIL_STORAGE_KEY = 'bug-reporter-email';

/**
 * Create dynamic form schema based on widget config
 */
const createFormSchema = (config: WidgetConfig) => {
  // Email required-ness is per-client: driven by widget config `requiredFields`
  // (same mechanism as screenshot). When required, it must be a valid address.
  // When optional, an empty value is allowed but a non-empty value must still
  // be a valid email. (013-pro-activation wrongly hardcoded this as required;
  // reverted to honour per-client configuration.)
  const emailRequired = (config.requiredFields || []).includes('email');
  // Both branches must yield a ZodString (no union) so zodResolver's generic
  // constraint holds. Optional branch: regex also matches the empty string.
  const emailRegex = emailRequired
    ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    : /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailSchema = emailRequired
    ? z.string().min(1, 'Email is required').max(254).regex(emailRegex, 'Invalid email address')
    : z.string().max(254).regex(emailRegex, 'Invalid email address');

  return z.object({
    title: z
      .string()
      .min(5, 'Title must be at least 5 characters')
      .max(200, 'Title must be at most 200 characters'),
    description: z
      .string()
      .min(config.minDescriptionLength, `Description must be at least ${config.minDescriptionLength} characters`)
      .max(config.maxDescriptionLength, `Description must be at most ${config.maxDescriptionLength} characters`),
    email: emailSchema,
  });
};

type FormData = {
  title: string;
  description: string;
  email: string; // required-ness is per-client (widget config requiredFields)
  website?: string; // Honeypot field - should always be empty
};

/**
 * Props for BugReportDialog component
 */
export interface BugReportDialogProps {
  /** Whether dialog is open */
  isOpen: boolean;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** API key for authentication */
  apiKey: string;

  /** Base URL for bug reporter API */
  apiBaseUrl: string;

  /** Cloudflare Turnstile site key */
  turnstileSiteKey: string;

  /** Widget configuration */
  widgetConfig: WidgetConfig;
}

/**
 * Bug report submission dialog
 *
 * Features:
 * - React Hook Form with Zod validation
 * - Screenshot capture and annotation
 * - Cloudflare Turnstile bot protection
 * - Submit button disabled until all requirements met
 * - Success/error toast notifications
 * - Mobile-responsive design
 * - Accessibility (ARIA labels, focus management)
 *
 * @example
 * ```tsx
 * <BugReportDialog
 *   isOpen={true}
 *   onClose={() => setDialogOpen(false)}
 *   apiKey="bw_live_abc123..."
 *   apiBaseUrl="https://api.example.com"
 *   turnstileSiteKey="your-site-key"
 * />
 * ```
 */
export const BugReportDialog: React.FC<BugReportDialogProps> = ({
  isOpen,
  onClose,
  apiKey,
  apiBaseUrl,
  turnstileSiteKey,
  widgetConfig,
}) => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  // Attachments state management
  const {
    attachments,
    count: attachmentCount,
    totalSize: attachmentTotalSize,
    addAttachment,
    removeAttachment,
    clearAttachments,
  } = useAttachments();

  // Preview modal state
  const [selectedAttachment, setSelectedAttachment] = useState<ClientAttachment | null>(null);

  // Attachment loading and error states
  const [isProcessingAttachments, setIsProcessingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  /**
   * Handle files dropped via DropZone
   * Adds each file as an upload attachment with loading state and error handling
   */
  const handleFilesDrop = useCallback(
    async (files: File[]) => {
      console.info('[BugReportDialog] Files dropped:', {
        count: files.length,
        files: files.map((f) => ({ name: f.name, size: f.size })),
      });

      setIsProcessingAttachments(true);
      setAttachmentError(null);

      // Process files sequentially to provide proper validation feedback
      let firstError: string | null = null;
      for (const file of files) {
        const result = addAttachment(file, 'upload' as AttachmentType);
        if (!result.success && result.error?.message && !firstError) {
          // Capture first error but continue processing valid files
          firstError = result.error.message;
          console.warn('[BugReportDialog] Attachment validation failed:', {
            fileName: file.name,
            error: result.error,
          });
        }
      }

      setIsProcessingAttachments(false);

      // Show first error if any files failed validation
      if (firstError) {
        setAttachmentError(firstError);
      }
    },
    [addAttachment]
  );

  /**
   * Handle attachment click
   * Opens preview modal for the attachment
   */
  const handleAttachmentClick = useCallback(
    (attachment: ClientAttachment) => {
      console.info('[BugReportDialog] Attachment clicked for preview:', {
        id: attachment.id,
        type: attachment.type,
      });
      setSelectedAttachment(attachment);
    },
    []
  );

  /**
   * Handle attachment remove
   * Removes attachment from the list
   */
  const handleAttachmentRemove = useCallback(
    (attachmentId: string) => {
      console.info('[BugReportDialog] Removing attachment:', attachmentId);
      removeAttachment(attachmentId);
    },
    [removeAttachment]
  );

  /**
   * Handle preview modal close
   */
  const handlePreviewClose = useCallback(() => {
    console.info('[BugReportDialog] Closing preview modal');
    setSelectedAttachment(null);
  }, []);

  // Create dynamic form schema based on widget config
  const formSchema = useMemo(() => createFormSchema(widgetConfig), [widgetConfig]);

  // Email and screenshot required-ness are both driven by per-client config
  const isEmailRequired = (widgetConfig.requiredFields || []).includes('email');
  const isScreenshotRequired = (widgetConfig.requiredFields || []).includes('screenshot');

  // Calculate header text color based on brand color luminance
  const brandColor = widgetConfig.brandColor || '#3b82f6';
  const headerTextColor = isLightColor(brandColor) ? '#1f2937' : '#ffffff';

  // Get saved email from sessionStorage
  const getSavedEmail = useCallback(() => {
    try {
      return sessionStorage.getItem(EMAIL_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }, []);

  // React Hook Form - use onTouched mode so isValid updates after interaction
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    trigger,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched', // Validate on blur and on submit
    defaultValues: {
      title: '',
      description: '',
      email: getSavedEmail(),
      website: '', // Honeypot field - always empty for real users
    },
  });

  // Watch email to persist it
  const emailValue = watch('email');

  // Watch description for character counter
  const descriptionValue = watch('description') || '';

  // Persist email to sessionStorage when it changes
  useEffect(() => {
    if (emailValue) {
      try {
        sessionStorage.setItem(EMAIL_STORAGE_KEY, emailValue);
      } catch {
        // Ignore storage errors
      }
    }
  }, [emailValue]);

  /**
   * Handle screenshot capture button click
   *
   * CRITICAL: We must capture the screenshot BEFORE showing any overlay!
   * The overlay would be captured in the screenshot if visible.
   *
   * Flow:
   * 1. Start capture immediately (no loading overlay shown yet)
   * 2. After capture completes, show canvas with the image
   *
   * The "capturing" state is only used to disable the button, NOT to show overlay.
   */
  const handleCaptureClick = useCallback(async () => {
    try {
      setErrorMessage(null);

      // Capture screenshot IMMEDIATELY without any overlay
      // The dialog will be hidden via hideDialog which uses showCanvas state
      const captured = await captureViewport();

      // Now that we have the screenshot, show the canvas
      setCapturedImage(captured);
      setShowCanvas(true);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('[BugReportDialog] Screenshot capture failed:', error);

      // Provide helpful error message
      let userMessage = 'Failed to capture screenshot.';
      if (errorMsg.includes('timeout')) {
        userMessage += ' The page took too long to render. Please try a simpler page or refresh.';
      } else if (errorMsg.includes('Memory')) {
        userMessage += ' The page is too large. Please scroll to a smaller section and try again.';
      } else if (errorMsg.includes('unsupported color')) {
        userMessage += ' Your page uses unsupported CSS color functions. Please refresh and try again.';
      } else if (errorMsg.includes('CORS')) {
        userMessage += ' Unable to load some page resources. Check your internet connection.';
      }

      setErrorMessage(userMessage);
    }
  }, []);

  /**
   * Handle screenshot save from canvas
   * Converts base64 to File and adds to attachments list
   */
  const handleScreenshotSave = useCallback((base64: string) => {
    // Convert base64 to File and add to attachments
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const file = dataUrlToFile(base64, `screenshot-${timestamp}.png`);

    const result = addAttachment(file, 'screenshot' as AttachmentType);
    if (!result.success) {
      console.error('[BugReportDialog] Failed to add screenshot to attachments:', result.error);
      // Fall back to legacy screenshot field if attachments fail
      setScreenshot(base64);
    } else {
      console.info('[BugReportDialog] Screenshot added to attachments:', result.attachmentId);
      // Clear legacy screenshot field since it's now in attachments
      setScreenshot(null);
    }

    setShowCanvas(false);
    setScreenshotError(null); // Clear error when screenshot is captured
  }, [addAttachment]);

  /**
   * Handle screenshot canvas cancel
   */
  const handleScreenshotCancel = useCallback(() => {
    setShowCanvas(false);
    setCapturedImage(null);
  }, []);

  /**
   * Handle screenshot remove
   */
  const handleScreenshotRemove = useCallback(() => {
    setScreenshot(null);
  }, []);

  /**
   * Handle Turnstile success
   */
  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  /**
   * Handle Turnstile error
   */
  const handleTurnstileError = useCallback((error: string) => {
    setErrorMessage(`Verification failed: ${error}`);
    setTurnstileToken(null);
  }, []);

  /**
   * Handle form submission
   */
  const onSubmit = useCallback(
    async (data: FormData) => {
      // Check if screenshot is required but missing (check both legacy field and attachments)
      const hasAnyScreenshot = !!screenshot || attachments.some(att => att.type === 'screenshot');
      if (isScreenshotRequired && !hasAnyScreenshot) {
        setScreenshotError('Screenshot is required');
        return;
      }
      setScreenshotError(null);

      if (!turnstileToken) {
        setErrorMessage('Please complete verification');
        return;
      }

      try {
        setIsSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        // Sanitize form data to prevent XSS attacks
        const sanitizedData = sanitizeFormData({
          title: data.title,
          description: data.description,
          email: data.email,
        });

        // Collect metadata
        const metadata = collectMetadata();

        // Prepare submission data
        const submitData: SubmitBugReportData = {
          title: sanitizedData.title,
          description: sanitizedData.description,
          email: sanitizedData.email, // was silently dropped — now forwarded to the API
          screenshot: screenshot,
          attachments: attachments,
          browserInfo: `${metadata.browserName} ${metadata.browserVersion}`,
          viewportSize: `${metadata.viewportWidth}x${metadata.viewportHeight}`,
          pageUrl: metadata.pageUrl,
          userAgent: metadata.userAgent,
          turnstileToken: turnstileToken,
          website: data.website || '', // Honeypot field - should be empty
        };

        // Submit bug report
        await submitBugReport(apiBaseUrl, apiKey, submitData);

        // Show custom success message from config
        setSuccessMessage(widgetConfig.successMessage);

        // Reset form after 2 seconds (keep email)
        setTimeout(() => {
          const savedEmail = getSavedEmail();
          reset({
            title: '',
            description: '',
            email: savedEmail,
            website: '',
          });
          setScreenshot(null);
          setTurnstileToken(null);
          setSuccessMessage(null);
          setAttachmentError(null);
          clearAttachments();
          onClose();
        }, 2000);
      } catch (error) {
        console.error('[BugReportDialog] Submission failed:', error);
        const errorMsg = getErrorMessage(error);
        setErrorMessage(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [screenshot, turnstileToken, apiBaseUrl, apiKey, reset, onClose, isScreenshotRequired, widgetConfig.successMessage, clearAttachments]
  );

  /**
   * Handle dialog close
   */
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      // Preserve email when closing dialog
      const savedEmail = getSavedEmail();
      reset({
        title: '',
        description: '',
        email: savedEmail,
        website: '',
      });
      setScreenshot(null);
      setTurnstileToken(null);
      setSuccessMessage(null);
      setErrorMessage(null);
      setScreenshotError(null);
      setAttachmentError(null);
      clearAttachments();
      setSelectedAttachment(null);
      onClose();
    }
  }, [isSubmitting, reset, onClose, getSavedEmail, clearAttachments]);

  /**
   * Check if submit button should be disabled
   * Considers: form validity, turnstile token, screenshot requirement (if configured)
   */
  // Check for screenshots in both legacy field and attachments
  const hasScreenshot = !!screenshot || attachments.some(att => att.type === 'screenshot');
  const isSubmitDisabled = !isValid || !turnstileToken || isSubmitting || (isScreenshotRequired && !hasScreenshot);

  // Debug: log what's blocking submit
  console.log('[BugReportDialog] Submit state:', {
    isValid,
    hasTurnstileToken: !!turnstileToken,
    isSubmitting,
    isSubmitDisabled,
    errors,
  });

  if (!isOpen) {
    return null;
  }

  // Hide dialog while showing canvas for annotation
  const hideDialog = showCanvas;

  return (
    <>
      {/* Dialog Overlay */}
      <div
        className="bug-reporter-dialog-overlay"
        onClick={handleClose}
        role="presentation"
        style={{ display: hideDialog ? 'none' : undefined }}
      >
        {/* Dialog */}
        <div
          className="bug-reporter-dialog"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-reporter-dialog-title"
          data-bug-reporter-modal
        >
          {/* Header */}
          <div
            className="bug-reporter-dialog__header"
            data-bug-reporter-modal-header
            style={{ color: headerTextColor }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {widgetConfig.logoSvg && (
                <div
                  dangerouslySetInnerHTML={{ __html: widgetConfig.logoSvg }}
                  aria-hidden="true"
                  data-bug-reporter-logo
                  style={{
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: headerTextColor,
                  }}
                />
              )}
              <h2 id="bug-reporter-dialog-title" className="bug-reporter-dialog__title">
                Report a Bug
              </h2>
            </div>
            <button
              type="button"
              className="bug-reporter-dialog__close"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Close dialog"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="bug-reporter-dialog__body">
            <form className="bug-reporter-form" onSubmit={handleSubmit(onSubmit)}>
              {/* Form fields - hidden when success message is shown */}
              {!successMessage && (
                <>
              {/* Left Column - Text fields */}
              <div className="bug-reporter-form__left-column">
                {/* Title Field */}
                <div className="bug-reporter-form__group">
                  <label htmlFor="bug-title" className="bug-reporter-form__label">
                    Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    id="bug-title"
                    type="text"
                    className="bug-reporter-form__input"
                    placeholder="Brief description of the bug"
                    {...register('title')}
                    disabled={isSubmitting}
                    aria-invalid={!!errors.title}
                    aria-describedby={errors.title ? 'title-error' : undefined}
                  />
                  {errors.title && (
                    <span id="title-error" className="bug-reporter-form__error" role="alert">
                      {errors.title.message}
                    </span>
                  )}
                </div>

                {/* Description Field */}
                <div className="bug-reporter-form__group">
                  <label htmlFor="bug-description" className="bug-reporter-form__label">
                    Description <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    id="bug-description"
                    className="bug-reporter-form__textarea"
                    placeholder="Detailed description of what went wrong..."
                    rows={4}
                    {...register('description')}
                    disabled={isSubmitting}
                    aria-invalid={!!errors.description}
                    aria-describedby={errors.description ? 'description-error' : undefined}
                    data-bug-reporter-description
                  />
                  {/* Character Counter */}
                  <span
                    data-bug-reporter-counter
                    className={`bug-reporter-form__counter ${
                      descriptionValue.length < widgetConfig.minDescriptionLength ||
                      descriptionValue.length > widgetConfig.maxDescriptionLength
                        ? 'bug-reporter-form__counter--error'
                        : ''
                    }`}
                    style={{
                      fontSize: 12,
                      color:
                        descriptionValue.length < widgetConfig.minDescriptionLength ||
                        descriptionValue.length > widgetConfig.maxDescriptionLength
                          ? '#ef4444'
                          : '#6b7280',
                    }}
                  >
                    {descriptionValue.length}/{widgetConfig.maxDescriptionLength}
                  </span>
                  {errors.description && (
                    <span id="description-error" className="bug-reporter-form__error" role="alert">
                      {errors.description.message}
                    </span>
                  )}
                </div>

                {/* Email Field */}
                <div className="bug-reporter-form__group">
                  <label
                    htmlFor="bug-email"
                    className="bug-reporter-form__label"
                    data-bug-reporter-email-label
                  >
                    Email {isEmailRequired ? <span style={{ color: '#ef4444' }}>*</span> : '(optional)'}
                  </label>
                  <input
                    id="bug-email"
                    type="email"
                    className="bug-reporter-form__input"
                    placeholder={isEmailRequired ? 'your@email.com' : 'your@email.com (optional)'}
                    {...register('email')}
                    disabled={isSubmitting}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    aria-required={isEmailRequired ? 'true' : undefined}
                    data-bug-reporter-email
                  />
                  {errors.email && (
                    <span
                      id="email-error"
                      className="bug-reporter-form__error"
                      role="alert"
                      data-bug-reporter-email-error
                    >
                      {errors.email.message}
                    </span>
                  )}
                </div>

                {/* Honeypot Field - Hidden from real users, bots will fill it */}
                <input
                  type="text"
                  {...register('website')}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    width: 0,
                    height: 0,
                  }}
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                />
              </div>

              {/* Right Column - Screenshot, Turnstile, Submit */}
              <div className="bug-reporter-form__right-column">
                {/* Screenshot Section - Screenshots are added to attachments */}
                <div className="bug-reporter-form__group" data-bug-reporter-screenshot-section>
                  <label className="bug-reporter-form__label">
                    Screenshot {isScreenshotRequired ? <span style={{ color: '#ef4444' }}>* Required</span> : <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(optional)</span>}
                  </label>

                  {/* Always show capture button unless attachments limit is reached */}
                  <button
                    type="button"
                    className="bug-reporter-form__button bug-reporter-form__button--secondary"
                    onClick={handleCaptureClick}
                    disabled={isSubmitting || attachmentCount >= 10}
                    data-bug-reporter-screenshot-btn
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 20, height: 20, marginRight: 8 }}
                      aria-hidden="true"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    {attachments.some(att => att.type === 'screenshot') ? 'Capture Another Screenshot' : 'Capture Screenshot'}
                  </button>

                  {/* Legacy screenshot preview (fallback if attachments fail) */}
                  {screenshot && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                      <img
                        src={screenshot}
                        alt="Screenshot preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          borderRadius: 6,
                          border: '1px solid #e5e7eb',
                          objectFit: 'contain',
                        }}
                        data-bug-reporter-screenshot-preview
                      />
                      <button
                        type="button"
                        className="bug-reporter-form__button bug-reporter-form__button--secondary"
                        onClick={handleScreenshotRemove}
                        disabled={isSubmitting}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ width: 16, height: 16, marginRight: 6 }}
                          aria-hidden="true"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        Remove Screenshot
                      </button>
                    </div>
                  )}

                  {/* Screenshot error */}
                  {screenshotError && (
                    <span
                      className="bug-reporter-form__error"
                      role="alert"
                      data-bug-reporter-screenshot-error
                    >
                      {screenshotError}
                    </span>
                  )}
                </div>

                {/* Attachments Section */}
                <div className="bug-reporter-form__group" data-bug-reporter-attachments-section>
                  <label className="bug-reporter-form__label">
                    Attachments <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(optional)</span>
                  </label>

                  <DropZone
                    onFilesDrop={handleFilesDrop}
                    disabled={isSubmitting || isProcessingAttachments || attachmentCount >= 10}
                    maxFiles={10 - attachmentCount}
                  />

                  {/* Attachment error message */}
                  {attachmentError && (
                    <span
                      className="bug-reporter-form__error"
                      role="alert"
                      data-bug-reporter-attachment-error
                      style={{ marginTop: 8, display: 'block' }}
                    >
                      {attachmentError}
                    </span>
                  )}

                  {/* Processing indicator */}
                  {isProcessingAttachments && (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 13,
                        color: '#6b7280',
                      }}
                      data-bug-reporter-attachment-loading
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          border: '2px solid rgba(107, 114, 128, 0.3)',
                          borderTopColor: '#6b7280',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                        }}
                        aria-hidden="true"
                      />
                      <span>Processing files...</span>
                    </div>
                  )}

                  {/* Attachment count and size info */}
                  {attachmentCount > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#6b7280',
                        marginTop: 8,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{attachmentCount}/10 attachments</span>
                      <span>{(attachmentTotalSize / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                  )}

                  {/* Attachment Grid */}
                  {attachmentCount > 0 && (
                    <AttachmentGrid
                      attachments={attachments}
                      onAttachmentClick={handleAttachmentClick}
                      onAttachmentRemove={handleAttachmentRemove}
                      maxAttachments={10}
                      disabled={isSubmitting || isProcessingAttachments}
                    />
                  )}
                </div>

                {/* Turnstile Widget */}
                <div className="bug-reporter-form__group">
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileError}
                  />
                </div>
              </div>
                </>
              )}

              {/* Success Message */}
              {successMessage && (
                <div
                  role="alert"
                  className="bug-reporter-form__full-width"
                  style={{
                    padding: 12,
                    backgroundColor: '#d1fae5',
                    color: '#065f46',
                    borderRadius: 6,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  data-bug-reporter-success
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 20, height: 20, flexShrink: 0 }}
                    aria-hidden="true"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Error Message - hidden when success */}
              {!successMessage && errorMessage && (
                <div
                  role="alert"
                  className="bug-reporter-form__full-width"
                  style={{
                    padding: 12,
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: 6,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 20, height: 20, flexShrink: 0 }}
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit Button - hidden when success, use aria-disabled to allow click events for validation */}
              {!successMessage && (
              <button
                type="submit"
                className="bug-reporter-form__button bug-reporter-form__full-width"
                aria-disabled={isSubmitDisabled}
                onClick={(e) => {
                  if (isSubmitDisabled) {
                    e.preventDefault();
                    // Trigger validation on click to show errors even when button is disabled
                    trigger();
                    // Check screenshot requirement (both legacy field and attachments)
                    if (isScreenshotRequired && !hasScreenshot) {
                      setScreenshotError('Screenshot is required');
                    }
                    return;
                  }
                }}
                style={{
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: isSubmitDisabled ? 0.5 : 1,
                  cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                  backgroundColor: brandColor,
                  color: headerTextColor,
                  border: `1px solid ${headerTextColor}`,
                }}
                data-bug-reporter-submit
              >
                {isSubmitting ? (
                  <>
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        border: `2px solid ${headerTextColor === '#ffffff' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'}`,
                        borderTopColor: headerTextColor,
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Bug Report'
                )}
              </button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Screenshot Canvas (Full-screen overlay) - Rendered via Portal outside Shadow DOM */}
      {/* Portal renders directly to document.body to ensure canvas is above all host page elements */}
      {showCanvas && capturedImage && (
        <Portal>
          <ScreenshotCanvas
            initialImage={capturedImage}
            onSave={handleScreenshotSave}
            onCancel={handleScreenshotCancel}
          />
        </Portal>
      )}

      {/* Attachment Preview Modal - Rendered via Portal outside Shadow DOM */}
      {selectedAttachment && (
        <Portal>
          <AttachmentPreview
            attachment={selectedAttachment}
            onClose={handlePreviewClose}
          />
        </Portal>
      )}

      {/* CSS for spinner animations */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes bug-reporter-spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </>
  );
};
